import React, { useState } from 'react';
import { AlertCircle, ImageIcon, Loader2, RotateCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useIntake } from '../context/IntakeContext';
import PhotoViewer from './PhotoViewer';
import { Button } from './ui/button';

const CATEGORY_LABELS = {
  vehicle: 'Vehicle',
  registration: 'Rego',
  damage: 'Damage',
  extra: 'Extra'
};

/**
 * Everything the customer has added, in whatever state it is in: uploaded,
 * uploading, or failed. Failures stay on screen with a retry rather than
 * disappearing — a photo that silently vanished is worse than one that says
 * it needs another go.
 *
 * Each photo is a button that opens it for review, where it can be replaced or
 * deleted. That replaced a delete button revealed on hover, which on a phone is
 * either invisible or permanently in the way.
 */
const PhotoGrid = ({ emptyHint = 'No photos yet' }) => {
  const { photos, uploads, replacePhoto, removePhoto, retryUpload, discardUpload } = useIntake();
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleReplace = async (file) => {
    const target = viewing;
    setViewing(null);
    setBusy(true);
    const result = await replacePhoto(target.id, file);
    setBusy(false);
    if (!result.ok) toast.error(result.error || 'That photo did not upload');
  };

  const handleDelete = async () => {
    const target = viewing;
    setViewing(null);
    try {
      await removePhoto(target.id);
    } catch (err) {
      toast.error(err.message || 'Could not remove that photo');
    }
  };

  if (!photos.length && !uploads.length) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg bg-muted/50 px-6 py-10 text-center">
        <ImageIcon className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{emptyHint}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setViewing(photo)}
            aria-label={`View ${photo.label || 'photo'}`}
            className="relative aspect-square overflow-hidden rounded-lg bg-muted transition-opacity hover:opacity-90"
          >
            {photo.preview ? (
              <img src={photo.preview} alt={photo.label || 'Photo'} className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full items-center justify-center">
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              </span>
            )}

            <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {CATEGORY_LABELS[photo.category] || 'Photo'}
            </span>
          </button>
        ))}

        {uploads.map((upload) => (
          <figure key={upload.key} className="relative aspect-square overflow-hidden rounded-lg bg-muted">
            {upload.preview && (
              <img src={upload.preview} alt="" className="h-full w-full object-cover opacity-40" />
            )}

            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2 text-center">
              {upload.status === 'uploading' ? (
                <Loader2 className="h-5 w-5 animate-spin text-foreground/70" />
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="secondary" className="h-7 px-2" onClick={() => retryUpload(upload.key)}>
                      <RotateCw className="h-3 w-3" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => discardUpload(upload.key)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </figure>
        ))}
      </div>

      {viewing && (
        <PhotoViewer
          photo={viewing}
          busy={busy}
          onReplace={handleReplace}
          onDelete={handleDelete}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
};

export default PhotoGrid;
